import { FC, useContext, useEffect } from 'react';
import PageLayout from '../../layout/PageLayout/PageLayout';
import Card from '../../components/Card/Card';
import { IconSuccess } from '../../icons/Icons';
import PasswordInput from '../../components/PasswordInput/PasswordInput';
import PasswordValidator from '../../components/PasswordValidator/PasswordValidator';
import { Redirect } from 'react-router-dom';
import { ERoutes, EQueryStringParam } from '../../util/constants';
import ResetPasswordStaffContext, {
    ResetPasswordStaffProvider,
    EResetPasswordStaffUiState,
} from '../../contexts/ResetPasswordStaffContext';
import styles from './ResetPasswordStaffScreen.module.scss';
import common from '../../util/scss/common.module.scss';
import API from '../../api/api';
import GlobalContext from '../../contexts/GlobalContext';
import { Alert, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';

const ResetPasswordStaffSuccess: FC = () => {
    const { language, fullLoginLink } = useContext(GlobalContext);
    return (
        <Card>
            <section className={common.icon}>
                <IconSuccess width={'50'} height={'50'} fillColor={'777982'} />
            </section>
            <header>
                <h1 className={`${common.pageHeader} ${common.withSubhead}`}>
                    {language.resetPassword.success.header}
                </h1>
                <h2 className={`${common.pageSubhead}`}>{language.resetPassword.success.subheader}</h2>
            </header>
            <Button
                component={'button'}
                variant="contained"
                size="large"
                onClick={() => window.location.assign(fullLoginLink)}
                fullWidth
                sx={{ mb: 3 }}
            >
                {language.resetPassword.success.cta}
            </Button>
        </Card>
    );
};

const ResetPasswordStaffForm: FC = () => {
    const { language, getFullLoginLinkFromLocalStorage } = useContext(GlobalContext);
    const {
        resetPasswordStaffUiState,
        password,
        setPassword,
        passwordIsValid,
        setPasswordHasFocus,
        setPasswordHasValidated,
        password2,
        setPassword2,
        password2IsValid,
        setPassword2HasFocus,
        setPassword2HasValidated,
        password2ValidationMessage,
        disableSubmit,
        submitResetPasswordForm,
    } = useContext(ResetPasswordStaffContext);

    const handlePasswordFocus = () => {
        setPasswordHasFocus(true);
    };

    const handlePassword2Focus = () => {
        setPassword2HasFocus(true);
    };

    const handlePasswordBlur = () => {
        setPasswordHasValidated(true);
        setPasswordHasFocus(false);
    };

    const handlePassword2Blur = () => {
        setPassword2HasValidated(true);
        setPassword2HasFocus(false);
    };

    const handleSubmitResetPassword = (event) => {
        event.preventDefault();
        API.post.trackEvent({ event: 'Sign In || Staff Update Password | Button - Update password' });
        submitResetPasswordForm();
    };

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Staff Update Password ' });
        getFullLoginLinkFromLocalStorage();
    }, []);

    return (
        <Card>
            <header>
                <h1 className={common.pageHeader}>{language.resetPassword.header}</h1>
            </header>
            {resetPasswordStaffUiState === EResetPasswordStaffUiState.error && (
                <section className={common.genericError}>
                    <Alert severity="error">{language.validation.genericError}</Alert>
                </section>
            )}
            <form>
                <main>
                    <PasswordInput
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        error={!passwordIsValid}
                        label={language.resetPassword.newPasswordLabel}
                        autoFocus={true}
                        onFocus={handlePasswordFocus}
                        onBlur={handlePasswordBlur}
                    />
                    <section className={`${styles.passwordValidatorBlock} ${styles.show}`}>
                        <div className={styles.passwordValidator}>
                            <PasswordValidator password={password} />
                        </div>
                    </section>
                    <PasswordInput
                        value={password2}
                        onChange={(event) => setPassword2(event.target.value)}
                        error={password2IsValid}
                        label={language.resetPassword.confirmNewPasswordLabel}
                        onFocus={handlePassword2Focus}
                        onBlur={handlePassword2Blur}
                        helperText={!password2IsValid && password2ValidationMessage}
                    />
                </main>
                <footer className={styles.resetPasswordCTA}>
                    <LoadingButton
                        variant="contained"
                        type="submit"
                        disabled={disableSubmit || resetPasswordStaffUiState === EResetPasswordStaffUiState.loading}
                        loading={resetPasswordStaffUiState === EResetPasswordStaffUiState.loading}
                        onClick={handleSubmitResetPassword}
                        size="large"
                    >
                        {language.resetPassword.submitPasswordCTA}
                    </LoadingButton>
                </footer>
            </form>
        </Card>
    );
};

const ResetPasswordStaffUi: FC = () => {
    const { resetPasswordStaffUiState, customerAccountId } = useContext(ResetPasswordStaffContext);
    let resetPasswordStaffUi: React.ReactElement;
    switch (resetPasswordStaffUiState) {
        case EResetPasswordStaffUiState.default:
        case EResetPasswordStaffUiState.loading:
        case EResetPasswordStaffUiState.error:
            resetPasswordStaffUi = <ResetPasswordStaffForm />;
            break;
        case EResetPasswordStaffUiState.success:
            resetPasswordStaffUi = <ResetPasswordStaffSuccess />;
            break;
        case EResetPasswordStaffUiState.expired:
            resetPasswordStaffUi = (
                <Redirect
                    to={{
                        pathname: `${ERoutes.forgotPasswordStaff}`,
                        search: `${EQueryStringParam.customerAccountId}=${customerAccountId}&${EQueryStringParam.retry}=true`,
                    }}
                />
            );
            break;
        case EResetPasswordStaffUiState.fatalError:
            resetPasswordStaffUi = <Redirect to={ERoutes.error} />;
            break;
        default:
            throw new Error('Reset Password State set incorrectly');
    }
    return resetPasswordStaffUi;
};

const ResetPasswordStaffScreen: FC = () => {
    return (
        <ResetPasswordStaffProvider>
            <PageLayout>
                <ResetPasswordStaffUi />
            </PageLayout>
        </ResetPasswordStaffProvider>
    );
};

export default ResetPasswordStaffScreen;
